import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Clock, BellRinging, FileText, CalendarCheck, Signature,
  WarningCircle, Play, Coffee, Sun, SpinnerGap, Airplane, CheckCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Riyadh',
  });
};

// Returns { text, cls, Icon } describing the employee's current day state.
const dayStatus = (d, ar) => {
  if (!d) return null;
  if (d.on_leave_today) {
    return {
      text: ar ? 'أنت في إجازة معتمدة اليوم — نتمنى لك وقتاً طيباً' : 'You are on approved leave today',
      cls: 'bg-sky-100 text-sky-800 border-sky-300',
      Icon: Airplane,
    };
  }
  if (d.is_rest_day) {
    return {
      text: ar ? 'يوم راحة — استمتع بيومك' : 'Rest day — enjoy your day',
      cls: 'bg-purple-50 text-[#655dc6] border-[#C4BFF0]',
      Icon: Coffee,
    };
  }
  if (d.is_doctor) {
    return {
      text: ar ? 'طبيب — لا يلزمك تسجيل حضور' : 'Doctor — no clock-in required',
      cls: 'bg-purple-50 text-[#655dc6] border-[#C4BFF0]',
      Icon: Sun,
    };
  }
  const att = d.attendance;
  if (att?.has_open_session) {
    return {
      text: ar ? `على رأس العمل منذ ${fmtTime(att.clock_in)}` : `On duty since ${fmtTime(att.clock_in)}`,
      cls: 'bg-green-50 text-green-700 border-green-200',
      Icon: CheckCircle,
      lateNote: att.late_minutes > 0
        ? (ar ? `تأخرت اليوم ${att.late_minutes} دقيقة` : `Late today by ${att.late_minutes} min`)
        : null,
    };
  }
  if (att?.clock_in && att?.clock_out) {
    return {
      text: ar ? `انتهى دوامك اليوم — انصرفت ${fmtTime(att.clock_out)}` : `Work completed — clocked out ${fmtTime(att.clock_out)}`,
      cls: 'bg-gray-100 text-gray-500 border-gray-200',
      Icon: CheckCircle,
    };
  }
  if (d.is_flexible) {
    return {
      text: ar ? 'دوامك مرن اليوم — سجّل حضورك عند بدء العمل' : 'Flexible day — clock in when you start',
      cls: 'bg-amber-50 text-amber-800 border-amber-200',
      Icon: Clock,
    };
  }
  return {
    text: ar ? 'لم تسجّل حضورك بعد' : 'You have not clocked in yet',
    cls: 'bg-amber-50 text-amber-800 border-amber-200',
    Icon: WarningCircle,
    showPunchBtn: true,
  };
};

export default function MyTodayCard() {
  const { language, isRTL } = useLanguage();
  const ar = language === 'ar';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ackId, setAckId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/my-today`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const acknowledgeSummons = async (id) => {
    setAckId(id);
    try {
      await axios.post(`${API}/hr/summons/${id}/acknowledge`, {}, { withCredentials: true });
      toast.success(ar ? 'تم تأكيد اطلاعك على الطلب' : 'Request acknowledged');
      setData((prev) => ({
        ...prev,
        pending_summons: prev.pending_summons.filter((s) => s.id !== id),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || (ar ? 'حدث خطأ' : 'Error'));
    } finally {
      setAckId(null);
    }
  };

  if (loading || !data) return null;

  const status = dayStatus(data, ar);
  const weekdayLabel = new Date(`${data.date}T12:00:00`).toLocaleDateString(
    ar ? 'ar-SA' : 'en-US',
    { weekday: 'long', day: 'numeric', month: 'long' }
  );

  const counters = [
    {
      key: 'notifications',
      icon: BellRinging,
      label: ar ? 'إشعارات غير مقروءة' : 'Unread notifications',
      value: data.unread_notifications,
      onClick: null,
    },
    {
      key: 'leaves',
      icon: CalendarCheck,
      label: ar ? 'إجازات قيد الموافقة' : 'Pending leaves',
      value: data.my_pending_leaves,
      onClick: () => navigate('/employee/leaves'),
    },
    {
      key: 'requests',
      icon: FileText,
      label: ar ? 'طلبات مفتوحة' : 'Open requests',
      value: data.my_open_requests,
      onClick: () => navigate('/employee/requests'),
    },
    {
      key: 'clearances',
      icon: Signature,
      label: ar ? 'مخالصات بانتظار توقيعك' : 'Clearances to sign',
      value: data.pending_signature_clearances,
      onClick: () => navigate('/employee/leaves'),
    },
  ];

  return (
    <Card className="border border-[#C4BFF0] mb-3 overflow-hidden" data-testid="my-today-card">
      <CardContent className="p-4 sm:p-5">
        {/* Header row */}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-base sm:text-lg font-bold text-[#655dc6] flex items-center gap-2">
            <Sun size={22} weight="duotone" />
            {ar ? 'يومي' : 'My Today'}
            <span className="text-xs font-normal text-[#655dc6]/60" data-testid="my-today-date">{weekdayLabel}</span>
          </h3>
          {/* Today's shifts */}
          {data.shifts?.length > 0 && !data.on_leave_today && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="my-today-shifts">
              {data.shifts.map((s, i) => (
                <span
                  key={i}
                  dir="ltr"
                  className="inline-flex items-center gap-1 text-[11px] font-mono bg-[#E8E6F5] text-[#655dc6] px-2.5 py-1 rounded-full"
                >
                  <Clock size={12} />
                  {s.start} → {s.end}
                  {data.shifts.length > 1 && (
                    <span className="opacity-60">({ar ? `وردية ${i + 1}` : `Shift ${i + 1}`})</span>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* HR Summons alert — highest priority */}
        {(data.pending_summons || []).map((s) => (
          <div
            key={s.id}
            className="mb-3 p-3 rounded-lg bg-red-50 border border-red-300 flex items-start justify-between gap-3"
            data-testid={`summons-alert-${s.id}`}
          >
            <div className="flex items-start gap-2">
              <WarningCircle size={20} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold text-red-700">
                  {ar ? 'طلب حضور من شؤون الموظفين' : 'HR Office Visit Request'}
                </p>
                <p className="text-red-700/90 text-xs mt-0.5">
                  {s.message || (ar ? 'يرجى مراجعة قسم شؤون الموظفين' : 'Please visit the HR department')}
                  {s.appointment_at && (
                    <span className="block mt-0.5" dir="ltr">
                      🗓 {s.appointment_at.replace('T', ' ')}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => acknowledgeSummons(s.id)}
              disabled={ackId === s.id}
              className="bg-red-600 hover:bg-red-700 text-white h-8 shrink-0"
              data-testid={`ack-summons-${s.id}`}
            >
              {ackId === s.id ? <SpinnerGap size={16} className="animate-spin" /> : (ar ? 'تأكيد الاطلاع' : 'Acknowledge')}
            </Button>
          </div>
        ))}

        {/* Current day status */}
        {status && (
          <div
            className={`p-3 rounded-lg border flex items-center justify-between gap-3 flex-wrap ${status.cls}`}
            data-testid="my-today-status"
          >
            <div className="flex items-center gap-2 text-sm font-semibold">
              <status.Icon size={20} weight="duotone" />
              <span>{status.text}</span>
              {status.lateNote && (
                <span className="text-[11px] font-normal bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                  {status.lateNote}
                </span>
              )}
            </div>
            {status.showPunchBtn && (
              <Button
                size="sm"
                onClick={() => navigate('/employee/dashboard')}
                className="bg-[#655dc6] hover:bg-[#544cb0] text-white h-8 gap-1.5"
                data-testid="my-today-punch-btn"
              >
                <Play size={14} weight="fill" />
                {ar ? 'سجّل حضورك الآن' : 'Clock in now'}
              </Button>
            )}
          </div>
        )}

        {/* Quick counters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          {counters.map((c) => (
            <button
              key={c.key}
              onClick={c.onClick || undefined}
              disabled={!c.onClick}
              className={`p-2.5 rounded-lg border text-start flex items-center gap-2.5 transition-colors ${
                c.value > 0
                  ? 'bg-[#E8E6F5] border-[#C4BFF0]'
                  : 'bg-white border-[#C4BFF0]/40 opacity-70'
              } ${c.onClick ? 'hover:bg-[#DCD8F7] cursor-pointer' : 'cursor-default'}`}
              data-testid={`my-today-counter-${c.key}`}
            >
              <div className="relative shrink-0 w-8 h-8 flex items-center justify-center">
                <c.icon size={22} weight="duotone" className="text-[#655dc6]" />
                {c.value > 0 && (
                  <span
                    className={`absolute -top-1 ${isRTL ? '-left-1' : '-right-1'} min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white shadow-sm leading-none`}
                    data-testid={`counter-badge-${c.key}`}
                  >
                    {c.value > 99 ? '99+' : c.value}
                  </span>
                )}
              </div>
              <span className="text-[11px] leading-tight text-[#655dc6]/80 font-medium">{c.label}</span>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
